import type { HTMLAttributes, RefObject } from "react"
import {
  FieldError as AriaFieldError,
  Label as AriaLabel,
  Text as AriaText,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from "react-aria-components"
import { LabelAndDescriptionStack } from "./Field"

type TextareaFieldProps = AriaTextFieldProps & {
  description?: string
  errorMessage?: string
  label?: string
  placeholder?: string
  inputProps?: HTMLAttributes<HTMLTextAreaElement> & {
    inputRef?: RefObject<HTMLTextAreaElement | null>
  }
}

/**
 * A form field that allows users to input multiple lines of text.
 *
 * ```tsx
 * import { TextareaField } from "@tokenterminal/core/ui/input/TextareaField"
 *
 * <TextareaField
 *   label="Description"
 *   placeholder="Enter a description"
 * />
 * ```
 */

export const TextareaField = ({
  description,
  errorMessage,
  label,
  placeholder,
  inputProps,
  ...props
}: TextareaFieldProps) => {
  const { className: inputClassName, ...restInputProps } = inputProps || {}

  return (
    <AriaTextField className="tt-field-wrapper" {...props}>
      {(label || description) && (
        <LabelAndDescriptionStack>
          {label && <AriaLabel className="tt-field-label">{label}</AriaLabel>}
          {description && (
            <AriaText slot="description" className="tt-field-description">
              {description}
            </AriaText>
          )}
        </LabelAndDescriptionStack>
      )}
      <AriaTextArea
        ref={inputProps?.inputRef}
        className={`tt-textarea-input ${inputClassName}`}
        placeholder={placeholder}
        {...restInputProps}
      />
      <AriaFieldError className="tt-field-error">{errorMessage}</AriaFieldError>
    </AriaTextField>
  )
}
